import { useEffect, useState } from 'react';
import { ErrorMsg, Loading, Section } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { CHANNELS } from '../../lib/contentTypes';
import type { AccountKind, PublishingAccount, ToneOfVoice } from '../../lib/contentTypes';

type Settings = { values: { publishing_accounts: PublishingAccount[]; tone_of_voice: ToneOfVoice } };
const lines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);

/** Brand & templates → "Publishing accounts" and "Tone of voice" (owner edits; everyone reads). */
export function VoicePanel() {
  const [accounts, setAccounts] = useState<PublishingAccount[] | null>(null);
  const [tone, setTone] = useState<ToneOfVoice | null>(null);
  const [doText, setDoText] = useState('');
  const [dontText, setDontText] = useState('');
  useEffect(() => {
    api<Settings>('/api/settings').then((s) => {
      setAccounts(s.values.publishing_accounts);
      setTone(s.values.tone_of_voice);
      setDoText(s.values.tone_of_voice.do.join('\n'));
      setDontText(s.values.tone_of_voice.dont.join('\n'));
    });
  }, []);
  if (!accounts || !tone) return <Loading what="Loading the tone of voice" />;
  return (
    <>
      <AccountsEditor accounts={accounts} onSaved={setAccounts} />
      <ToneEditor tone={tone} setTone={setTone} doText={doText} setDoText={setDoText} dontText={dontText} setDontText={setDontText} />
    </>
  );
}

function AccountsEditor({ accounts, onSaved }: { accounts: PublishingAccount[]; onSaved: (a: PublishingAccount[]) => void }) {
  const [rows, setRows] = useState(accounts);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const edit = (i: number, patch: Partial<PublishingAccount>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  async function save() {
    setError('');
    setMsg('');
    try {
      const r = await api<{ value: PublishingAccount[] }>('/api/settings/publishing_accounts', { method: 'PUT', body: { value: rows } });
      setRows(r.value);
      onSaved(r.value);
      setMsg('Publishing accounts saved.');
    } catch (e) {
      setError(errorText(e));
    }
  }
  return (
    <Section title="Publishing accounts" id="accounts">
      <p className="muted small">The accounts a publication can have a text for. An account that a text uses cannot be removed: delete those texts first.</p>
      <ul className="plain cs-accounts">
        {rows.map((a, i) => (
          <li key={a.id || i} className="row gap center wrap" data-account={a.id}>
            <input value={a.label} maxLength={100} aria-label="Account label" onChange={(e) => edit(i, { label: e.target.value })} />
            <select value={a.channel} aria-label={`Channel of ${a.label}`} onChange={(e) => edit(i, { channel: e.target.value as PublishingAccount['channel'] })}>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select value={a.kind} aria-label={`Kind of ${a.label}`} onChange={(e) => edit(i, { kind: e.target.value as AccountKind })}>
              <option value="company">company</option>
              <option value="personal">personal</option>
            </select>
            <button type="button" className="btn ghost small" onClick={() => setRows(rows.filter((_, j) => j !== i))} disabled={rows.length <= 1}>
              Remove
            </button>
          </li>
        ))}
      </ul>
      <div className="row gap center">
        <button type="button" className="btn small" onClick={() => setRows([...rows, { id: '', label: '', channel: 'linkedin', kind: 'company' }])}>
          Add an account
        </button>
        <button type="button" className="btn primary small" onClick={() => void save()}>
          Save accounts
        </button>
        <span className="muted small">Owner only.</span>
        {msg && (
          <span className="ok-text small" role="status">
            {msg}
          </span>
        )}
      </div>
      <ErrorMsg>{error}</ErrorMsg>
    </Section>
  );
}

function ToneEditor(props: {
  tone: ToneOfVoice;
  setTone: (t: ToneOfVoice) => void;
  doText: string;
  setDoText: (s: string) => void;
  dontText: string;
  setDontText: (s: string) => void;
}) {
  const { tone, setTone, doText, setDoText, dontText, setDontText } = props;
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  async function save() {
    setError('');
    setMsg('');
    try {
      const r = await api<{ value: ToneOfVoice }>('/api/settings/tone_of_voice', {
        method: 'PUT',
        body: { value: { ...tone, do: lines(doText), dont: lines(dontText), examples: tone.examples.filter((e) => e.text.trim()) } },
      });
      setTone(r.value);
      setDoText(r.value.do.join('\n'));
      setDontText(r.value.dont.join('\n'));
      setMsg('Tone of voice saved. The model reads it with every suggestion.');
    } catch (e) {
      setError(errorText(e));
    }
  }
  const setExample = (i: number, patch: Partial<ToneOfVoice['examples'][number]>) =>
    setTone({ ...tone, examples: tone.examples.map((e, j) => (j === i ? { ...e, ...patch } : e)) });
  return (
    <Section title="Tone of voice" id="tone">
      <p className="muted small">How Mirage sounds. It is sent to the local model with each text suggestion, together with the project's public facts only (never the client or the internal notes).</p>
      <div className="form-grid" aria-label="Tone of voice">
        <label className="field span2">
          <span>How we sound (up to 2000 characters)</span>
          <textarea rows={4} maxLength={2000} value={tone.description} onChange={(e) => setTone({ ...tone, description: e.target.value })} aria-label="Tone description" />
        </label>
        <label className="field">
          <span>Do (one per line)</span>
          <textarea rows={4} value={doText} onChange={(e) => setDoText(e.target.value)} aria-label="Tone do" />
        </label>
        <label className="field">
          <span>Don't (one per line)</span>
          <textarea rows={4} value={dontText} onChange={(e) => setDontText(e.target.value)} aria-label="Tone dont" />
        </label>
        <label className="field">
          <span>Emoji</span>
          <select value={tone.emoji} onChange={(e) => setTone({ ...tone, emoji: e.target.value as ToneOfVoice['emoji'] })} aria-label="Emoji policy">
            <option value="none">none</option>
            <option value="sparing">sparing</option>
            <option value="free">free</option>
          </select>
        </label>
        <div className="row gap">
          <label className="field">
            <span>Hashtags, at most</span>
            <input type="number" min={0} max={30} value={tone.hashtags.max} onChange={(e) => setTone({ ...tone, hashtags: { ...tone.hashtags, max: Number(e.target.value) } })} aria-label="Hashtag maximum" />
          </label>
          <label className="field grow">
            <span>Hashtag style</span>
            <input value={tone.hashtags.style} maxLength={200} onChange={(e) => setTone({ ...tone, hashtags: { ...tone.hashtags, style: e.target.value } })} placeholder="CamelCase, project name first…" aria-label="Hashtag style" />
          </label>
        </div>
        <label className="field">
          <span>Company accounts (the studio speaks as “we”)</span>
          <textarea rows={3} maxLength={1000} value={tone.kind_notes.company} onChange={(e) => setTone({ ...tone, kind_notes: { ...tone.kind_notes, company: e.target.value } })} aria-label="Company account notes" />
        </label>
        <label className="field">
          <span>Personal accounts (a person at Mirage, first person)</span>
          <textarea rows={3} maxLength={1000} value={tone.kind_notes.personal} onChange={(e) => setTone({ ...tone, kind_notes: { ...tone.kind_notes, personal: e.target.value } })} aria-label="Personal account notes" />
        </label>
        <fieldset className="field span2">
          <legend>Example texts in your tone (up to 10)</legend>
          <ul className="plain stack">
            {tone.examples.map((e, i) => (
              <li key={i} className="row gap wrap">
                <textarea className="grow" rows={3} maxLength={2000} value={e.text} onChange={(ev) => setExample(i, { text: ev.target.value })} aria-label={`Example text ${i + 1}`} />
                <select value={e.kind ?? ''} onChange={(ev) => setExample(i, { kind: (ev.target.value || null) as AccountKind | null })} aria-label={`Account kind of example ${i + 1}`}>
                  <option value="">any account</option>
                  <option value="company">company</option>
                  <option value="personal">personal</option>
                </select>
                <button type="button" className="btn ghost small" onClick={() => setTone({ ...tone, examples: tone.examples.filter((_, j) => j !== i) })}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
          <div>
            <button type="button" className="btn small" disabled={tone.examples.length >= 10} onClick={() => setTone({ ...tone, examples: [...tone.examples, { text: '', kind: null }] })}>
              Add an example
            </button>
          </div>
        </fieldset>
        <div className="span2 row gap center">
          <button type="button" className="btn primary" onClick={() => void save()}>
            Save tone of voice
          </button>
          <span className="muted small">Owner only.</span>
          {msg && (
            <span className="ok-text small" role="status">
              {msg}
            </span>
          )}
        </div>
        <div className="span2">
          <ErrorMsg>{error}</ErrorMsg>
        </div>
      </div>
    </Section>
  );
}
